import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Calendar as CalendarIcon, ChevronLeft, ChevronRight, MapPin,
  Users, Clock, Plus, Loader2, List, Edit, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { formatTimeRange, formatDateMMDDYYYY, toLocalYMD, enumerateDates } from '../../utils/timeFormat';

const STATUS_STYLES = {
  open: { dot: 'bg-amber-500', label: 'Open' },
  partially_filled: { dot: 'bg-blue-500', label: 'Partial' },
  filled: { dot: 'bg-emerald-500', label: 'Filled' },
  cancelled: { dot: 'bg-gray-400', label: 'Cancelled' },
  completed: { dot: 'bg-purple-500', label: 'Completed' },
};

const ClientCalendar = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'list' : 'calendar'
  );
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dayDrawer, setDayDrawer] = useState(null);

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/client/shifts?limit=500&include_past=true');
      setShifts(Array.isArray(res.data) ? res.data : res.data.shifts || []);
    } catch {
      toast.error('Failed to load your shifts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  // Re-fetch when the tab regains focus so a booking update made in
  // another tab (e.g. admin booked someone, shift was cancelled) is
  // reflected without a hard refresh.
  useEffect(() => {
    const onFocus = () => fetchShifts();
    const onVisibility = () => { if (!document.hidden) fetchShifts(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchShifts]);

  // Calendar math
  const year = selectedMonth.getFullYear();
  const month = selectedMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startWeekday = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  // Group shifts by every date they occupy (supports multi-day via end_date).
  // Iter-335 — a shift is only expanded across multiple days when BOTH:
  //   1) `duration_days > 1` (explicit multi-day intent from creation flow)
  //   2) `end_date` is a well-formed date strictly later than `shift_date`
  // Otherwise it renders only on `shift_date`. This defensively prevents a
  // stray `end_date` from duplicating a single-day gig across the month.
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  const shiftsByDate = shifts.reduce((acc, s) => {
    if (!s.shift_date || !dateRegex.test(s.shift_date)) return acc;
    const durationDays = Number(s.duration_days || 1);
    const endValid = typeof s.end_date === 'string'
      && dateRegex.test(s.end_date)
      && s.end_date > s.shift_date;
    const isMultiDay = durationDays > 1 && endValid;
    const days = isMultiDay ? enumerateDates(s.shift_date, s.end_date) : [s.shift_date];
    for (const d of days) (acc[d] = acc[d] || []).push(s);
    return acc;
  }, {});

  const navigateMonth = (dir) => {
    const next = new Date(selectedMonth);
    next.setMonth(next.getMonth() + dir);
    setSelectedMonth(next);
  };

  const handleDayClick = (dateStr) => {
    const daysShifts = shiftsByDate[dateStr] || [];
    if (daysShifts.length === 0) {
      // Prefill create shift
      navigate(`/client/shifts/create?prefill_date=${dateStr}`);
      return;
    }
    if (daysShifts.length === 1) {
      navigate(`/client/shifts/${daysShifts[0].id}`);
      return;
    }
    setDayDrawer({ date: dateStr, shifts: daysShifts });
  };

  // Build calendar cells
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push({ empty: true, key: `e-${i}` });
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, dateStr, shifts: shiftsByDate[dateStr] || [], key: dateStr });
  }

  const today = toLocalYMD(new Date());
  const monthName = selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // List view: upcoming + past grouped.
  // Cancelled shifts are treated as PAST regardless of date so they stop
  // showing up as active commitments (user explicitly asked for this).
  const sortedShifts = [...shifts].sort((a, b) => (a.shift_date || '').localeCompare(b.shift_date || ''));
  const isCancelled = (s) => s.status === 'cancelled';
  const effectiveEnd = (s) => s.end_date || s.shift_date || '';
  const upcoming = sortedShifts.filter((s) => !isCancelled(s) && effectiveEnd(s) >= today);
  const past = sortedShifts.filter((s) => isCancelled(s) || effectiveEnd(s) < today);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/40 to-white">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate('/client/home')}
              data-testid="client-cal-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Home
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <CalendarIcon className="w-6 h-6 text-cyan-500" /> My Calendar
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">
                View and manage your posted gigs. Click a day to see or edit shifts, or create a new one.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="inline-flex bg-white border border-gray-200 rounded-md p-0.5">
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-2.5 py-1 text-xs rounded ${viewMode === 'calendar' ? 'bg-cyan-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                data-testid="cal-view-calendar"
              >
                <CalendarIcon className="w-3 h-3 inline mr-1" /> Grid
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-2.5 py-1 text-xs rounded ${viewMode === 'list' ? 'bg-cyan-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                data-testid="cal-view-list"
              >
                <List className="w-3 h-3 inline mr-1" /> List
              </button>
            </div>
            <Button
              onClick={() => navigate('/client/shifts/create')}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              size="sm"
              data-testid="cal-create-shift"
            >
              <Plus className="w-4 h-4 mr-1" /> New Gig
            </Button>
          </div>
        </div>

        {loading && (
          <div className="py-20 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
          </div>
        )}

        {!loading && viewMode === 'calendar' && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)} data-testid="cal-prev-month">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <CardTitle className="text-lg">{monthName}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)} data-testid="cal-next-month">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-2 sm:p-4">
              <div className="grid grid-cols-7 gap-1 text-[10px] sm:text-xs font-medium text-gray-500 uppercase mb-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                  <div key={d} className="text-center py-1">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
                {cells.map((c) => {
                  if (c.empty) return <div key={c.key} className="aspect-square" />;
                  const isToday = c.dateStr === today;
                  const hasShifts = c.shifts.length > 0;
                  const hasActiveShifts = c.shifts.some((s) => s.status !== 'cancelled');
                  return (
                    <button
                      key={c.key}
                      onClick={() => handleDayClick(c.dateStr)}
                      className={`relative min-h-[64px] sm:min-h-[96px] rounded-md border text-left p-1 sm:p-1.5 transition-colors
                        ${isToday ? 'border-cyan-500 ring-1 ring-cyan-300' : 'border-gray-200'}
                        ${hasActiveShifts ? 'bg-cyan-50/70 hover:bg-cyan-100' : hasShifts ? 'bg-gray-50 hover:bg-gray-100' : 'bg-white hover:bg-gray-50'}
                      `}
                      data-testid={`cal-day-${c.dateStr}`}
                    >
                      <div className={`text-xs sm:text-sm font-semibold ${isToday ? 'text-cyan-700' : 'text-gray-800'}`}>
                        {c.day}
                      </div>
                      <div className="mt-1 space-y-0.5">
                        {c.shifts.slice(0, 2).map((s) => {
                          const st = STATUS_STYLES[s.status] || STATUS_STYLES.open;
                          const cancelled = s.status === 'cancelled';
                          return (
                            <div
                              key={s.id}
                              className={`flex items-center gap-1 text-[9px] sm:text-[10px] truncate ${cancelled ? 'opacity-50 line-through' : ''}`}
                              data-testid={`cal-cell-shift-${s.id}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${st.dot}`} />
                              <span className="truncate text-gray-700">{s.role_name || s.venue_name || 'Shift'}</span>
                            </div>
                          );
                        })}
                        {c.shifts.length > 2 && (
                          <div className="text-[9px] text-gray-500">+{c.shifts.length - 2} more</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex flex-wrap gap-2 text-[10px] text-gray-500">
                {Object.entries(STATUS_STYLES).map(([k, v]) => (
                  <span key={k} className="inline-flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${v.dot}`} /> {v.label}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {!loading && viewMode === 'list' && (
          <div className="space-y-4">
            {upcoming.length === 0 && past.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center text-gray-500 text-sm">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  You haven't posted any gigs yet.
                  <div className="mt-3">
                    <Button onClick={() => navigate('/client/shifts/create')} className="bg-cyan-500 hover:bg-cyan-600 text-white" size="sm">
                      Post Your First Gig
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            {upcoming.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Upcoming ({upcoming.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {upcoming.map((s) => (
                    <ShiftRow key={s.id} shift={s} onClick={() => navigate(`/client/shifts/${s.id}`)} />
                  ))}
                </CardContent>
              </Card>
            )}
            {past.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-600">Past ({past.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {past.slice(0, 20).map((s) => (
                    <ShiftRow key={s.id} shift={s} onClick={() => navigate(`/client/shifts/${s.id}`)} past />
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Day drawer — shown when a day has multiple shifts */}
      {dayDrawer && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setDayDrawer(null)}
          data-testid="cal-day-drawer-backdrop"
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] overflow-y-auto p-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="cal-day-drawer"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">
                {new Date(`${dayDrawer.date}T12:00:00`).toLocaleDateString('en-US', {
                  weekday: 'long', month: 'long', day: 'numeric',
                })}
              </h3>
              <button onClick={() => setDayDrawer(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="space-y-2">
              {dayDrawer.shifts.map((s) => (
                <ShiftRow key={s.id} shift={s} onClick={() => { setDayDrawer(null); navigate(`/client/shifts/${s.id}`); }} />
              ))}
              <Button
                className="w-full mt-2 bg-cyan-500 hover:bg-cyan-600 text-white"
                onClick={() => navigate(`/client/shifts/create?prefill_date=${dayDrawer.date}`)}
                data-testid="cal-drawer-add-shift"
              >
                <Plus className="w-4 h-4 mr-1" /> Add a shift on this day
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ShiftRow = ({ shift, onClick, past }) => {
  const st = STATUS_STYLES[shift.status] || STATUS_STYLES.open;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-md border flex items-start justify-between gap-3 transition-colors
        ${past ? 'bg-gray-50 border-gray-100' : 'bg-white border-gray-200 hover:border-cyan-300'}`}
      data-testid={`cal-shift-row-${shift.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`w-2 h-2 rounded-full ${st.dot}`} />
          <p className="text-sm font-medium text-gray-900 truncate">{shift.role_name || shift.venue_name || 'Shift'}</p>
          <Badge variant="outline" className="text-[10px]">{st.label}</Badge>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDateMMDDYYYY(shift.shift_date)} · {formatTimeRange(shift.start_time, shift.end_time)}</span>
          {shift.venue_name && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {shift.venue_name}</span>}
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {shift.talent_needed || 1} talent</span>
        </div>
      </div>
      <Edit className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
    </button>
  );
};

export default ClientCalendar;
